// src/constants/recordTypes.js
//
// Single source of truth for health_records.record_type — used by the
// patient upload page, the navigator/doctor HealthRecordsTab, and the new
// chat document-type picker (Sprint 15).
//
// 17 Jul 2026: extracted after finding the two existing copies
// (PatientUploadPage.jsx, HealthRecordsTab.js) had already drifted —
// PatientUploadPage.jsx was missing VACCINATION, which HealthRecordsTab.js
// had. Any future record type must be added here once, plus:
//   1. HealthRecord.java's RecordType enum (backend)
//   2. The chk_record_type CHECK constraint (Flyway migration — see V55)
// All three are independent sources of truth that must stay in lockstep.

export const RECORD_TYPES = [
  { value: 'BLOOD_TEST',        label: 'Blood Test',    icon: '🩸', primary: true },
  { value: 'PRESCRIPTION',      label: 'Prescription',  icon: '💊' },
  { value: 'MEDICAL_BILL',      label: 'Medical Bill',  icon: '🧾' },
  { value: 'XRAY',              label: 'X-Ray',         icon: '🫁' },
  { value: 'MRI',               label: 'MRI',           icon: '🧲' },
  { value: 'CT_SCAN',           label: 'CT Scan',       icon: '🖥️' },
  { value: 'ECG',               label: 'ECG',           icon: '💓' },
  { value: 'DISCHARGE_SUMMARY', label: 'Discharge',     icon: '📋' },
  { value: 'VACCINATION',       label: 'Vaccination',   icon: '💉' },
  { value: 'OTHER',             label: 'Other',         icon: '📄' },
];

// A shorter list for low-friction surfaces (chat quick-reply chips) where
// showing all 10 types would be too much — covers what patients actually
// upload most; "Other" always available as a catch-all.
export const RECORD_TYPES_QUICK = RECORD_TYPES.filter(t =>
  ['BLOOD_TEST', 'PRESCRIPTION', 'MEDICAL_BILL', 'XRAY', 'OTHER'].includes(t.value)
);

export const getTypeConfig = (recordType) =>
  RECORD_TYPES.find(t => t.value === recordType) || RECORD_TYPES[RECORD_TYPES.length - 1];

// ─────────────────────────────────────────────────────────────────────────────
// Filename-based type inference — DEF-015
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Keywords per type as they appear in FILENAMES, most-specific type first.
 *
 * Mirrors RecordTypeInferencer.FILENAME_KEYWORDS on the server, deliberately.
 * The server infers from the document's extracted TEXT (which the browser does
 * not have at attach time) as well as its filename; this client copy exists only
 * to pre-select a chip the instant a file is attached, so the patient confirms a
 * suggestion instead of classifying from scratch.
 *
 * The two lists agreeing is not load-bearing: the server never trusts a
 * client-supplied type it did not ask for, and a wrong pre-selection is one tap
 * to correct. If they drift, the cost is a worse suggestion, not a wrong record.
 *
 * Ordering matters — a discharge summary mentions medications, so it is tested
 * before PRESCRIPTION can claim it.
 */
const FILENAME_KEYWORDS = [
  ['DISCHARGE_SUMMARY', ['discharge']],
  ['VACCINATION',       ['vaccination', 'vaccine', 'immunisation', 'immunization', 'covishield', 'covaxin']],
  ['MEDICAL_BILL',      ['invoice', 'bill', 'receipt', 'payment']],
  ['PRESCRIPTION',      ['prescription', 'rx']],
  ['ECG',               ['ecg', 'ekg', 'electrocardiogram']],
  ['CT_SCAN',           ['ct', 'hrct', 'cect']],
  ['MRI',               ['mri']],
  ['XRAY',              ['x ray', 'xray', 'radiograph']],
  ['BLOOD_TEST',        ['blood', 'bloodwork', 'cbc', 'lipid', 'haematology', 'hematology',
                         'lft', 'kft', 'thyroid', 'hba1c', 'pathology', 'lab']],
];

/**
 * Whole-word containment. Without this, PRESCRIPTION's "rx" matches inside any
 * identifier and CT_SCAN's "ct" matches inside "document" — the same substring
 * collisions that made the server's old text matching unusable (see DEF-022).
 */
function containsWord(haystack, word) {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`).test(haystack);
}

/**
 * Best-guess record type from a filename, or null when nothing matches.
 *
 * DEF-015 / TC-P015 — "Document type NOT auto-inferred on upload. User asked to
 * manually select: Blood Test / Prescription / Medical Bill / X-Ray / Other."
 * Returning null is a real answer: with no signal, no chip is pre-selected and
 * the patient picks, which is better than pre-selecting BLOOD_TEST and having
 * them accept a wrong default without reading it.
 */
export const inferRecordTypeFromFileName = (fileName) => {
  if (!fileName) return null;
  const stem = String(fileName)
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
  for (const [type, keywords] of FILENAME_KEYWORDS) {
    if (keywords.some(k => containsWord(stem, k))) return type;
  }
  return null;
};
